import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users as UsersIcon, TrendingUp, UserPlus, Activity } from 'lucide-react';
import { adminApi, type AdminUser, type PaginatedResponse } from '../../lib/api';
import { StatCard } from '../../components/admin/StatCard';
import { LoadingSkeleton } from '../../components/admin/LoadingSkeleton';
import { EmptyState } from '../../components/admin/EmptyState';

export function UsersPage() {
  const [users, setUsers] = useState<PaginatedResponse<AdminUser> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminApi.getUsers({
          page,
          search: search || undefined,
        });
        setUsers(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, search]);

  // Compute stats from loaded data
  const stats = useMemo(() => {
    if (!users) return null;

    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const byStatus = users.data.reduce(
      (acc, user) => {
        acc[user.status] = (acc[user.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const newUsersCount = users.data.filter(
      (user) => new Date(user.createdAt) > sevenDaysAgo,
    ).length;

    const activeCount = byStatus['active'] || 0;

    return {
      total: users.pagination.total,
      active: activeCount,
      newUsers: newUsersCount,
      byStatus,
    };
  }, [users]);

  // Apply client-side filters
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    return users.data.filter((user) => {
      if (statusFilter && user.status !== statusFilter) return false;
      if (roleFilter && user.role !== roleFilter) return false;
      return true;
    });
  }, [users, statusFilter, roleFilter]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setRoleFilter('');
    setSearch('');
    setPage(1);
  };

  const hasActiveFilters = statusFilter || roleFilter || search;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-xl sm:text-2xl text-parchment">Users</h1>
          <p className="text-mist text-sm mt-1">Manage user accounts and permissions</p>
        </div>
      </div>

      {/* Summary Statistics */}
      {isLoading ? (
        <LoadingSkeleton variant="grid" />
      ) : stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={stats.total} icon={UsersIcon} />
          <StatCard
            label="Active"
            value={stats.active}
            subtitle={`${((stats.active / stats.total) * 100).toFixed(0)}% of total`}
            icon={Activity}
            variant="success"
          />
          <StatCard label="New (7d)" value={stats.newUsers} icon={UserPlus} variant="info" />
          <StatCard
            label="By Status"
            value={Object.keys(stats.byStatus).length}
            subtitle={`Active: ${stats.byStatus['active'] || 0}, Waitlisted: ${stats.byStatus['waitlisted'] || 0}`}
            icon={TrendingUp}
          />
        </div>
      ) : null}

      {/* Filters */}
      <div className="bg-night border border-stone/30 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email..."
              className="flex-1 px-4 py-2 bg-void border border-stone/30 rounded-lg text-parchment placeholder-mist/50 text-sm focus:outline-none focus:border-rune"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-rune/20 text-rune border border-rune/30 rounded-lg hover:bg-rune/30 transition-colors text-sm whitespace-nowrap"
            >
              Search
            </button>
          </form>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="waitlisted">Waitlisted</option>
            <option value="suspended">Suspended</option>
            <option value="deleted">Deleted</option>
          </select>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-stone/30 text-mist rounded-lg hover:bg-stone/50 transition-colors text-sm whitespace-nowrap"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-ember/10 border border-ember/30 rounded-lg text-ember">{error}</div>
      )}

      {/* Users Table */}
      {isLoading ? (
        <LoadingSkeleton variant="table" rows={10} cols={7} />
      ) : filteredUsers.length === 0 ? (
        <EmptyState
          variant={hasActiveFilters ? 'no-results' : 'no-data'}
          title={hasActiveFilters ? 'No users match your filters' : 'No users found'}
          description={
            hasActiveFilters ? 'Try adjusting your search or filter criteria.' : undefined
          }
          action={
            hasActiveFilters ? { label: 'Clear Filters', onClick: handleClearFilters } : undefined
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
                <table className="w-full min-w-[900px] text-sm">
                  <thead>
                    <tr className="text-mist text-left border-b border-stone/30 bg-stone/20">
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Role</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-center">Worlds</th>
                      <th className="px-4 py-3 font-medium text-center">Daily Quota</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                      <th className="px-4 py-3 font-medium">Last Modified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-stone/20 last:border-0 hover:bg-stone/10 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/users/${user.id}`}
                            className="text-rune hover:text-rune-glow hover:underline font-medium"
                          >
                            {user.email}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              user.role === 'admin'
                                ? 'bg-rune/20 text-rune'
                                : 'bg-stone/30 text-mist'
                            }`}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              user.status === 'active'
                                ? 'bg-success/20 text-success'
                                : user.status === 'waitlisted'
                                  ? 'bg-rune/20 text-rune'
                                  : user.status === 'suspended'
                                    ? 'bg-ember/20 text-ember'
                                    : 'bg-stone/30 text-mist'
                            }`}
                          >
                            {user.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Link
                            to={`/admin/worlds?user=${user.id}`}
                            className="text-parchment hover:text-rune transition-colors"
                          >
                            {user.worldCount || 0}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-center text-parchment">
                          {user.dailyLetterQuota}
                        </td>
                        <td className="px-4 py-3 text-mist text-xs">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-mist text-xs">
                          {formatRelativeTime(user.updatedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {users && users.pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-mist">
                Showing {filteredUsers.length} of {users.pagination.total} users
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-3 py-2 bg-stone/30 text-mist rounded-lg text-sm focus:outline-none focus:border-rune"
                >
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-2.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-2.5 text-sm text-mist">
                  Page {page} of {users.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === users.pagination.totalPages}
                  className="px-3 py-2.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString();
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
